import React, { createContext, useContext } from 'react';
import { useAdminAuth, AdminRole } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Shield, ShieldAlert, LogOut, Lock } from 'lucide-react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ApiQuotaAlertBanner } from './ApiQuotaAlertBanner';
import { AdminPortalNav } from './AdminPortalNav';

interface AdminGateProps {
  children: React.ReactNode;
  requiredRole?: AdminRole;
}

const AdminShellContext = createContext<boolean>(false);

export function getValidAdminNextUrl(rawNext: string | null): string {
  if (!rawNext) return '/admin';
  try {
    const decoded = decodeURIComponent(rawNext).trim();
    if (
      decoded.startsWith('/admin') &&
      !decoded.startsWith('//') &&
      !decoded.startsWith('\\') &&
      !decoded.includes('://') &&
      !decoded.includes('\r') &&
      !decoded.includes('\n')
    ) {
      return decoded;
    }
  } catch (e) {
    // Ignore decode error
  }
  return '/admin';
}

export function AdminGate({ children, requiredRole }: AdminGateProps) {
  const hasShell = useContext(AdminShellContext);
  const { 
    user, 
    role, 
    isAuthorized, 
    isSuperAdmin, 
    loading, 
    authError, 
    loginWithGoogle, 
    loginWithMicrosoft, 
    signOut,
    clearError
  } = useAdminAuth();
  const location = useLocation();

  // During auth resolution
  if (loading) {
    return (
      <div className="min-h-screen bg-ground flex flex-col items-center justify-center p-6">
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <div className="w-12 h-12 border-4 border-rule-3 border-t-transparent rounded-full animate-spin mb-4" />
      </div>
    );
  }

  // Check if current user has an active authenticated session with an admin claim
  const hasAdminClaim = !!user && isAuthorized;

  // Case 1: Unauthenticated or not authorized with an admin claim
  if (!hasAdminClaim) {
    // If signed in but not authorized (non-admin account)
    if (user && !isAuthorized) {
      return (
        <div className="min-h-screen bg-ground-alt flex items-center justify-center px-4 py-16">
          <Helmet>
            <title>Admin Unauthorized | FIVE LENS Enneagram</title>
            <meta name="robots" content="noindex,nofollow" />
          </Helmet>
          <div className="max-w-md w-full bg-white border border-ground-deep shadow-2xl p-8 md:p-10 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-rose-500" />
            <div className="w-16 h-16 bg-rose-50 text-rose-600 border border-rose-200 rounded-full mx-auto flex items-center justify-center mb-6">
              <ShieldAlert className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-serif font-bold text-ink mb-2">Access Denied</h1>
            <p className="text-xs uppercase tracking-widest text-rose-600 font-bold mb-4">Unauthorized Account</p>
            
            <p className="text-sm text-ink/80 mb-8 leading-relaxed">
              This account (<span className="font-semibold text-ink">{user.email}</span>) isn&apos;t authorised for admin access. Contact the site owner.
            </p>

            <Button 
              onClick={signOut}
              className="w-full bg-ink hover:bg-ink/90 text-white flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </Button>
          </div>
        </div>
      );
    }

    // Unauthenticated user attempting to access /admin/login or any /admin/* URL
    const isLoginPage = location.pathname === '/admin/login';
    if (!isLoginPage) {
      const fullPath = location.pathname + location.search;
      const loginUrl = `/admin/login?next=${encodeURIComponent(fullPath)}`;
      return <Navigate to={loginUrl} replace />;
    }

    // On /admin/login page: render login screen
    return (
      <div className="min-h-screen bg-ground-alt flex items-center justify-center px-4 py-16">
        <Helmet>
          <title>Admin Sign In | FIVE LENS Enneagram</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <div className="max-w-md w-full bg-white border border-ground-deep shadow-2xl p-8 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-ink via-rule-3 to-ink" />

          {/* Logo & Heading */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-ground-alt border border-ground-deep rounded-2xl mx-auto flex items-center justify-center mb-4 text-ink shadow-inner">
              <Shield className="w-7 h-7 text-rule-3" />
            </div>
            <h1 className="text-2xl font-serif font-bold text-ink mb-2">FIVE LENS Admin Portal</h1>
            <p className="text-xs uppercase tracking-widest text-ink/70 font-semibold">Authorized Personnel Only</p>
          </div>

          {authError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm flex items-start gap-3">
              <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">{authError}</p>
              </div>
              <button onClick={clearError} className="text-red-500 hover:text-red-700 text-xs font-bold">×</button>
            </div>
          )}

          {/* Social Logins */}
          <div className="space-y-3 mb-6">
            <Button
              type="button"
              variant="outline"
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 py-3 border-ground-deep hover:border-ink hover:bg-ground-alt transition-colors text-ink font-semibold"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              Sign in with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={loginWithMicrosoft}
              className="w-full flex items-center justify-center gap-3 py-3 border-ground-deep hover:border-ink hover:bg-ground-alt transition-colors text-ink font-semibold"
            >
              <svg className="w-4 h-4" viewBox="0 0 23 23">
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              Sign in with Microsoft
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated & authorized (hasAdminClaim)
  // Check if we were on /admin/login or if a `next` param is present
  const searchParams = new URLSearchParams(location.search);
  const rawNext = searchParams.get('next');
  if (location.pathname === '/admin/login' || rawNext) {
    const validTarget = getValidAdminNextUrl(rawNext);
    return <Navigate to={validTarget} replace />;
  }

  // Case 2: Required Super Admin role check
  if (requiredRole === 'Super Admin' && !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-ground-alt flex items-center justify-center px-4 py-16">
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <div className="max-w-md w-full bg-white border border-amber-300 shadow-2xl p-8 md:p-10 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-500" />
          <div className="w-16 h-16 bg-amber-50 text-amber-600 border border-amber-200 rounded-full mx-auto flex items-center justify-center mb-6">
            <Lock className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-ink mb-2">Super Admin Required</h1>
          <p className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-4">Restricted Feature</p>
          
          <div className="bg-ground-alt p-4 border border-ground-deep text-left text-sm mb-6 rounded-sm">
            <p className="text-ink/70 text-xs uppercase tracking-wider font-semibold mb-1">Your Role:</p>
            <p className="font-bold text-ink">{role || 'Viewer'}</p>
          </div>

          <p className="text-sm text-ink/70 mb-8 leading-relaxed">
            User Access and Administrative Permissions management is restricted exclusively to Super Administrators. 
            Your current role has permission to access content and general management tools.
          </p>

          <Link to="/admin">
            <Button className="w-full bg-ink hover:bg-ink/90 text-white">
              Return to Admin Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Authorized with admin claim! Render dashboard with noindex,nofollow
  return (
    <AdminShellContext.Provider value={true}>
      <div className="admin-portal-shell pt-[82px]">
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        {!hasShell && <ApiQuotaAlertBanner />}
        {!hasShell && <AdminPortalNav />}
        <div className="admin-page-body [&>div]:!pt-6 [&>div]:!mt-0">
          {children}
        </div>
      </div>
    </AdminShellContext.Provider>
  );
}
